import { useTable } from "@tanstack/react-table";
import type React from "react";
import { useCallback, useMemo, useRef } from "react";

import { buildDriverColumns, type ColumnFilterOptions } from "@/components/DriverView/columns";
import { ampli } from "@/config";
import type { useDriverGridState } from "@/hooks/driver/useDriverGridState";
import type { DriverRow, TagDefinition } from "@/types/driver";
import { buildCheckboxColumn } from "@/ui/DataTable";
import { type DataTableInstance, dataTableFeatures } from "@/ui/dataTableFeatures";

type DriverGridState = ReturnType<typeof useDriverGridState>;

/**
 * Builds the driver-grid TanStack table (columns + state wiring) once, so it can be shared by the grid renderer
 * (`DriverDataGrid`) and the column manager in the toolbar. Visibility/order/sizing/selection are controlled by
 * `useDriverGridState`; pinning is applied by `DataTable` from `gridState.columnPinning`.
 */
export function useDriverTable({
  drivers,
  gridState,
  getColumnExtra,
  isFilterActive,
  getActiveFilterValues,
  showUnreadOnly,
  toggleUnreadOnly,
  tags,
  shiftSelectRef,
  unactivatedMembershipIds,
  personalOnlyMembershipIds,
}: {
  drivers: DriverRow[];
  gridState: DriverGridState;
  getColumnExtra: ColumnFilterOptions["getColumnExtra"];
  isFilterActive: ColumnFilterOptions["isFilterActive"];
  getActiveFilterValues: ColumnFilterOptions["getActiveFilterValues"];
  showUnreadOnly: boolean;
  toggleUnreadOnly: () => void;
  tags: TagDefinition[];
  /** Shared shift-click selection anchor, so the `X` keyboard shortcut and checkbox clicks track one anchor. */
  shiftSelectRef: React.MutableRefObject<string | null>;
  unactivatedMembershipIds?: ReadonlySet<string>;
  personalOnlyMembershipIds?: ReadonlySet<string>;
}): DataTableInstance<DriverRow> {
  // Keep the filter callbacks behind stable refs so column identities don't churn every render. Rebuilt columns would
  // also remount the headers and close an open filter dropdown. The tag cells read the active filter through the ref
  // too. They stay current because a filter change rebuilds `drivers` (`useDriverQuery`), which gives every row new
  // data, and a row with new data re-renders (`rowPropsEqual`).
  const getColumnExtraRef = useRef(getColumnExtra);
  getColumnExtraRef.current = getColumnExtra;
  const stableGetColumnExtra = useCallback((key: string) => getColumnExtraRef.current(key), []);

  const isFilterActiveRef = useRef(isFilterActive);
  isFilterActiveRef.current = isFilterActive;
  const stableIsFilterActive = useCallback((key: string) => isFilterActiveRef.current(key), []);

  const getActiveFilterValuesRef = useRef(getActiveFilterValues);
  getActiveFilterValuesRef.current = getActiveFilterValues;
  const stableGetActiveFilterValues = useCallback((key: string) => getActiveFilterValuesRef.current(key), []);

  const columns = useMemo(
    () => [
      buildCheckboxColumn<DriverRow>({
        shiftSelectRef,
        onBulkSelect: () => ampli.bulkSelectDrivers(),
      }),
      ...buildDriverColumns(
        {
          getColumnExtra: stableGetColumnExtra,
          isFilterActive: stableIsFilterActive,
          getActiveFilterValues: stableGetActiveFilterValues,
          showUnreadOnly,
          toggleUnreadOnly,
          unactivatedMembershipIds,
          personalOnlyMembershipIds,
        },
        tags,
      ),
    ],
    [
      stableGetColumnExtra,
      stableIsFilterActive,
      stableGetActiveFilterValues,
      showUnreadOnly,
      toggleUnreadOnly,
      tags,
      unactivatedMembershipIds,
      personalOnlyMembershipIds,
    ],
  );

  return useTable({
    features: dataTableFeatures,
    data: drivers,
    columns,
    state: {
      columnVisibility: gridState.columnVisibility,
      columnOrder: gridState.columnOrder,
      columnSizing: gridState.columnSizing,
      rowSelection: gridState.rowSelection,
    },
    onColumnVisibilityChange: gridState.onColumnVisibilityChange,
    onColumnOrderChange: gridState.onColumnOrderChange,
    onColumnSizingChange: gridState.onColumnSizingChange,
    onRowSelectionChange: gridState.onRowSelectionChange,
    getRowId: (row) => row.workspaceMembershipId,
    // The roster renders every driver. The shared feature set registers the paginated row model, which
    // `getRowModel()` runs through, so opting out here is what keeps all rows rendered rather than page one.
    manualPagination: true,
    enableSorting: false,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
  });
}
